import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Check, Award, Users, Target, Heart } from 'lucide-react';
import { companyInfo, lakes, testimonials, droneImage } from '../data/mock';

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: "Excellence",
      description: "Nous utilisons la technologie la plus avancée pour garantir un service de qualité supérieure."
    },
    {
      icon: Heart,
      title: "Engagement",
      description: "Chaque intervention est réalisée avec soin et professionnalisme."
    },
    {
      icon: Target,
      title: "Précision",
      description: "Notre caméra HD permet un contrôle minutieux de chaque zone nettoyée."
    },
    {
      icon: Users,
      title: "Proximité",
      description: "Trois bureaux pour être au plus près de nos clients sur les lacs romands."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  À propos de nous
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
                  <span className="text-blue-600">{companyInfo.name}</span>
                </h1>
                <p className="text-slate-600 mb-6">
                  Spécialiste du nettoyage de coques de bateaux par drone sous-marin, nous intervenons sur le Lac Léman, le Lac de Neuchâtel et le Lac de Morat.
                </p>
                <p className="text-slate-600 mb-8">
                  Notre mission est de simplifier l'entretien de votre bateau grâce à une technologie innovante, écologique et efficace. Plus besoin de mise au sec coûteuse et contraignante !
                </p>
                
                <div className="flex flex-wrap gap-4">
                  {lakes.map((lake) => (
                    <span
                      key={lake.id}
                      className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {lake.name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="bg-white rounded-3xl shadow-2xl p-6">
                  <img
                    src={droneImage}
                    alt="Drone Keelcrab Pro"
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl" />
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-600/20 rounded-full blur-2xl" />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Nos valeurs
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Ce qui nous <span className="text-blue-600">distingue</span>
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Chez {companyInfo.name}, nous mettons un point d'honneur à offrir un service irréprochable.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <div
                    key={index}
                    className="text-center p-8 rounded-2xl hover:bg-slate-50 transition-colors border border-slate-100 hover:border-blue-100 hover:shadow-lg"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Nos avantages
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Pourquoi choisir <span className="text-blue-600">notre service ?</span>
                </h2>
                
                <ul className="space-y-4">
                  {[
                    'Intervention sans mise au sec - votre bateau reste à l\'eau',
                    'Réduction de 20-30% de votre consommation de carburant',
                    'Préservation de votre antifouling - carénage moins fréquent',
                    'Inspection vidéo HD fournie après chaque intervention',
                    'Technologie écologique - zéro rejet dans les lacs',
                    'Service rapide - nettoyage en quelques heures seulement',
                    'Trois bureaux pour couvrir les lacs romands'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://customer-assets.emergentagent.com/job_e5574598-9882-4c0d-a82a-daa0265bfbb4/artifacts/nydv55cc_Screenshot_20260103_035432_Chrome.jpg"
                      alt="Drone Keelcrab"
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://customer-assets.emergentagent.com/job_e5574598-9882-4c0d-a82a-daa0265bfbb4/artifacts/rvk7o8fg_Screenshot_20260103_035841_Chrome.jpg"
                      alt="Nettoyage de coque"
                      className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://customer-assets.emergentagent.com/job_e5574598-9882-4c0d-a82a-daa0265bfbb4/artifacts/metvr1xj_IMG-20260103-WA0002.jpg"
                      alt="Bateau à moteur"
                      className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://customer-assets.emergentagent.com/job_e5574598-9882-4c0d-a82a-daa0265bfbb4/artifacts/k0sw8gz7_IMG-20260103-WA0003.jpg"
                      alt="Voilier"
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Témoignages
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Ce que nos clients <span className="text-blue-600">disent</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100"
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic">"{testimonial.comment}"</p>
                  <div>
                    <p className="font-bold text-slate-800">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.boat}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
